import { PositioningStrategy } from '@popperjs/core';
import { useEffect, useRef, useState } from 'react';
import { PopperProps, usePopper } from 'react-popper';

interface GetPopperConfigProps {
  placement?: PopperProps<any>['placement'];
  strategy?: PositioningStrategy;
}

export const getPopperConfig = ({
  placement = 'bottom-start',
  strategy = 'absolute',
}: GetPopperConfigProps): Parameters<typeof usePopper>[2] => ({
  placement,
  strategy,
  modifiers: [
    {
      name: 'offset',
      enabled: true,
      options: {
        offset: [0, 10],
      },
    },
  ],
});

export type DropdownOptionType = {
  key: string;
  value: string;
};

export interface useDropdownProps {
  onSelect: (value: string) => void;
  options: DropdownOptionType[];
  selectedOption?: DropdownOptionType;
  placement?: GetPopperConfigProps['placement'];
}

const useDropdown = ({
  onSelect,
  options,
  placement,
  selectedOption,
}: useDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<string>(
    selectedOption?.key ?? ''
  );
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const prevOpen = useRef<boolean>(false);

  const { styles, attributes } = usePopper(
    triggerButtonRef.current,
    popperRef.current,
    getPopperConfig({ placement })
  );

  useEffect(() => {
    if (prevOpen.current !== isOpen) {
      prevOpen.current = isOpen;
      setHighlighted(selectedOption?.key ?? '');
    }
  }, [isOpen, selectedOption?.key]);

  const highlightItem = (itemIndex: number) => {
    if (isOpen) {
      const selectedItem = options[itemIndex];
      setHighlighted(selectedItem.key);
    } else {
      setIsOpen(true);
    }
  };

  const persistSelection = () => {
    onSelect(highlighted);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const highlightedIndex = options.findIndex(
      (option) => option.key === highlighted
    );
    let freeze = false;

    switch (event.key) {
      case 'ArrowDown':
        if (highlightedIndex < options.length - 1) {
          highlightItem(highlightedIndex + 1);
        }
        freeze = true;
        break;
      case 'ArrowUp':
        if (highlightedIndex > 0) {
          highlightItem(highlightedIndex - 1);
        }
        freeze = true;
        break;
      case 'Home':
        highlightItem(0);
        freeze = true;
        break;
      case 'End':
        highlightItem(options.length - 1);
        freeze = true;
        break;
      case 'Enter':
      case ' ':
        if (isOpen) {
          persistSelection();
        }
        setIsOpen((isOpen) => !isOpen);
        freeze = true;
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }

    if (freeze) {
      event.stopPropagation();
      event.preventDefault();
    }
  };

  return {
    isOpen,
    setIsOpen,
    handleKeyDown,
    popperRef,
    triggerButtonRef,
    styles,
    attributes,
    highlighted,
  };
};

export default useDropdown;
